<template>
  <div class="personas-page">
    <!-- Header -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">
          <span class="gradient-text">🎭 Persona Hub</span>
        </h1>
        <p class="page-subtitle">{{ $t('personas_page.subtitle') }}</p>

        <!-- Search / Filter -->
        <div class="filter-bar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search personas..."
              class="search-input"
            />
          </div>

          <!-- Language Tabs -->
          <div class="lang-tabs">
            <button
              class="lang-tab"
              :class="{ active: selectedLang === '' }"
              @click="selectedLang = ''"
            >All</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'ko' }"
              @click="selectedLang = 'ko'"
            >🇰🇷 한국어</button>
            <button
              class="lang-tab"
              :class="{ active: selectedLang === 'en' }"
              @click="selectedLang = 'en'"
            >🇺🇸 English</button>
          </div>

          <!-- Category Tabs -->
          <div class="category-tabs">
            <button
              class="cat-tab"
              :class="{ active: selectedCategory === '' }"
              @click="selectedCategory = ''"
            >
              All
            </button>
            <button
              class="cat-tab"
              v-for="cat in categories"
              :key="cat"
              :class="{ active: selectedCategory === cat }"
              @click="selectedCategory = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Grid -->
    <div class="container">
      <div class="persona-count" v-if="filteredPersonas.length">
        {{ $t('personas_page.count', { n: filteredPersonas.length }) }}
      </div>

      <div class="personas-grid" v-if="visiblePersonas.length">
        <PersonaCard
          v-for="(p, idx) in visiblePersonas"
          :key="p.id"
          :persona="p"
          class="animate-in"
          :style="{ animationDelay: (idx % 30) * 40 + 'ms' }"
          @select="openModal"
        />
      </div>

      <!-- Load More Sentinel -->
      <div
        v-if="hasMore"
        ref="sentinel"
        class="load-more-sentinel"
      >
        <div class="loading-spinner"></div>
      </div>

      <div class="empty-state" v-if="!visiblePersonas.length && !loading">
        <div class="empty-icon">🔍</div>
        <p>{{ $t('personas_page.empty') }}</p>
        <button class="btn-reset" @click="searchQuery = ''; selectedCategory = ''; selectedLang = ''">
          {{ $t('personas_page.reset_filters') }}
        </button>
      </div>
    </div>

    <!-- Modal -->
    <PersonaModal
      :visible="showModal"
      :persona="selectedPersona"
      :isLoggedIn="isLoggedIn"
      @close="closeModal"
      @downloaded="onDownloaded"
    />
  </div>
</template>

<script>
import PersonaCard from '../components/PersonaCard.vue'
import PersonaModal from '../components/PersonaModal.vue'
import { auth } from '../auth'

const GITHUB_STORE = 'https://raw.githubusercontent.com/xoul-project/xoul_store/main/dist'
const PAGE_SIZE = 30

export default {
  name: 'PersonasView',
  components: {
    PersonaCard,
    PersonaModal,
  },
  data() {
    return {
      personas: [],
      allPersonas: [],  // raw from GitHub
      categories: [],
      searchQuery: '',
      selectedCategory: '',
      selectedLang: '',
      showModal: false,
      selectedPersona: {},
      loading: true,
      displayCount: PAGE_SIZE,
      observer: null,
    }
  },
  computed: {
    isLoggedIn() {
      return auth.isLoggedIn()
    },
    filteredPersonas() {
      let items = this.personas
      if (this.selectedLang) {
        items = items.filter(p => (p.lang || 'en') === this.selectedLang)
      }
      if (this.selectedCategory) {
        items = items.filter(p => p.category === this.selectedCategory)
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        items = items.filter(
          p =>
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q)
        )
      }
      return items
    },
    visiblePersonas() {
      return this.filteredPersonas.slice(0, this.displayCount)
    },
    hasMore() {
      return this.displayCount < this.filteredPersonas.length
    },
  },
  watch: {
    searchQuery() { this.displayCount = PAGE_SIZE },
    selectedCategory() { this.displayCount = PAGE_SIZE },
    selectedLang() { this.displayCount = PAGE_SIZE },
    hasMore(val) {
      this.$nextTick(() => {
        if (val) this.observeSentinel()
        else this.unobserveSentinel()
      })
    },
  },
  async mounted() {
    await this.fetchPersonas()
    this.$nextTick(() => this.observeSentinel())
  },
  beforeUnmount() {
    this.unobserveSentinel()
  },
  methods: {
    observeSentinel() {
      if (this.observer) this.observer.disconnect()
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.displayCount += PAGE_SIZE
        }
      }, { rootMargin: '200px' })
      this.observer.observe(el)
    },
    unobserveSentinel() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    _resolveLang(val) {
      const lang = localStorage.getItem('xoul-locale') || 'en'
      if (typeof val === 'string') {
        try {
          const obj = JSON.parse(val)
          return obj[lang] || obj['en'] || obj['ko'] || val
        } catch { return val }
      }
      if (typeof val === 'object' && val) return val[lang] || val['en'] || val['ko'] || ''
      return val || ''
    },
    async fetchPersonas() {
      try {
        const res = await fetch(`${GITHUB_STORE}/personas.json`)
        const raw = await res.json()
        this.allPersonas = raw
        this.personas = raw.map(p => ({
          ...p,
          name: this._resolveLang(p.name),
          description: this._resolveLang(p.description),
          prompt_preview: (p.prompt || '').substring(0, 200),
        }))
        const catSet = new Set(raw.map(p => p.category).filter(Boolean))
        this.categories = [...catSet].sort()
      } catch (err) {
        console.error('Failed to fetch personas from GitHub:', err)
      } finally {
        this.loading = false
      }
    },
    openModal(persona) {
      const full = this.allPersonas.find(x => x.id === persona.id) || persona
      this.selectedPersona = {
        ...full,
        name: this._resolveLang(full.name),
        description: this._resolveLang(full.description),
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    onDownloaded(personaId) {
      const idx = this.personas.findIndex(p => p.id === personaId)
      if (idx !== -1) {
        this.personas[idx].downloads = (this.personas[idx].downloads || 0) + 1
      }
    },
  },
}
</script>

<style scoped>
.personas-page {
  min-height: 100vh;
  background: transparent;
  padding-bottom: 80px;
}

.page-header {
  background: rgba(10, 10, 10, 0.5);
  padding: 40px 0 32px;
  border-bottom: 1px solid #1a1a1a;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
  text-align: center;
}

.gradient-text {
  background: linear-gradient(135deg, #50c878, #70d890);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.page-subtitle {
  text-align: center;
  font-size: 16px;
  color: var(--subtext0);
  margin-bottom: 32px;
}

.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.search-box {
  display: flex;
  align-items: center;
  background: rgba(13, 13, 13, 0.7);
  border: 1px solid #1a1a1a;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 10px;
  max-width: 400px;
}

.search-icon {
  font-size: 16px;
}

.search-input {
  flex: 1;
  background: none;
  border: none;
  color: var(--text);
  font-size: 14px;
  outline: none;
}

.lang-tabs, .category-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.lang-tab, .cat-tab {
  background: transparent;
  border: 1px solid #1a1a1a;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  color: var(--subtext0);
  cursor: pointer;
  transition: all var(--transition);
}

.lang-tab:hover, .cat-tab:hover {
  border-color: rgba(80, 200, 120, 0.3);
  color: var(--text);
}

.lang-tab.active, .cat-tab.active {
  background: rgba(80, 200, 120, 0.12);
  color: #50c878;
  border-color: rgba(80, 200, 120, 0.3);
  font-weight: 600;
}

.persona-count {
  font-size: 14px;
  color: var(--subtext0);
  margin: 24px 0 16px;
}

.personas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.empty-state {
  text-align: center;
  padding: 60px 0;
  color: var(--subtext0);
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.btn-reset {
  margin-top: 16px;
  background: var(--surface1);
  border: none;
  color: var(--text);
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
}

.load-more-sentinel {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--surface2);
  border-top: 3px solid #50c878;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.animate-in {
  opacity: 0;
  animation: fadeSlideIn 0.4s ease forwards;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
